import { ContentType } from "@brookbush/database";
import { StructuredDataService } from "@brookbush/structured-data";
import type { Video } from "@brookbush/types";

import type { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useState } from "react";

import VideoAppBar from "./components/VideoAppBar";
import VideoBody from "./components/VideoBody";
import VideoDetails from "./components/VideoDetails";
import VideoTabs from "./components/VideoTabs";
import Comments from "~/components/Comments";
import Container from "~/components/Container";
import { useTrackHistory } from "~/hooks";
import useChatPageContent from "~/hooks/useChatPageContent";
import { videoPagePlayerChromeClassName } from "~/lib/video/videoFullscreenShell";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

interface VideoPageProps {
	video: Video;
	structuredData: string;
}

const VideoPage: NextPageWithProps<VideoPageProps> = ({
	video,
	structuredData,
}) => {
	useTrackHistory({
		contentId: video._id,
		contentType: ContentType.VIDEO,
	});

	useChatPageContent({
		contentType: ContentType.VIDEO,
		contentId: video._id,
		title: video.title,
	});

	const [selectedIndex, setSelectedIndex] = useState(0);

	return (
		<>
			<NextSeo
				title={video.title}
				description={video.description}
				canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/videos/${video.slug}`}
				openGraph={{
					title: video.title,
					url: `${process.env.NEXT_PUBLIC_SITE_URL}/videos/${video.slug}`,
					description: video.description,
					images: [
						{
							url: video.thumbnailUrl!,
							width: 800,
							height: 600,
							alt: video.title,
						},
					],
				}}
			/>

			<script
				type="application/ld+json"
				id="json-ld"
				dangerouslySetInnerHTML={{
					__html: structuredData,
				}}
			/>

			<Container key={video._id}>
				<div className="grid grid-cols-12 laptop:grid-rows-[auto_auto] gap-4 laptop:gap-6">
					<div className="col-span-12 laptop:col-span-8 -mb-1 laptop:-mb-6 flex flex-col gap-3 laptop:gap-6">
						<section className={videoPagePlayerChromeClassName()}>
							<VideoBody video={video} />
						</section>

						<VideoDetails video={video} />
					</div>

					<VideoTabs
						video={video}
						selectedIndex={selectedIndex}
						setSelectedIndex={setSelectedIndex}
					/>

					<div
						className="col-span-full laptop:col-span-8 scroll-m-[21rem]"
						id="video-comments"
					>
						<Comments contentId={video._id} contentType={ContentType.VIDEO} />
					</div>
				</div>
			</Container>

			<VideoAppBar video={video} setSelectedIndex={setSelectedIndex} />
		</>
	);
};

export const getStaticPaths: GetStaticPaths = async () => {
	const videoPaths = await fetcher("/videos/paths");
	return {
		paths: videoPaths.map((slug: string) => ({
			params: { slug: String(slug) },
		})),
		fallback: `blocking`,
	};
};

export const getStaticProps: GetStaticProps = async (req) => {
	try {
		const slug = req.params?.slug as string;

		const video: Video = await fetcher(`/videos/${slug}`);

		if (!video) {
			return {
				notFound: true,
			};
		}

		const structuredData = await Promise.all([
			StructuredDataService.getVideo(video),
			StructuredDataService.getBreadcrumbs(`/videos/${slug}`, (fragment) =>
				fragment === slug ? video.title : undefined,
			),
		]);

		for (const result of structuredData) {
			if (!result) {
				return {
					notFound: true,
				};
			}
		}

		return {
			props: {
				video,
				hasAppBar: true,
				breadcrumbs: [
					{
						name: "Videos",
						href: "/videos",
					},
					{
						name: video.title,
						href: `/videos/${video.slug}`,
					},
				],
				structuredData: StructuredDataService.withContext({
					"@graph": structuredData,
				}),
			},
		};
	} catch (_error) {
		return {
			notFound: true,
		};
	}
};

export default VideoPage;
